/**
 * Single source of truth for client-side UI preferences.
 *
 * All pref reads/writes (selected view, active filter, page size, sidebar
 * collapsed, theme, locale, calendar state, seating zoom, etc.) go through
 * this module. Backed by `localStorage`. The cockpit is a SPA on Cloudflare
 * Pages — there is no server reading these values, so cookies have no
 * advantage over localStorage and force every API request to ship a few
 * hundred bytes of irrelevant header.
 *
 * Auth tokens (HttpOnly, backend-set) and identity bootstraps (`companyId`,
 * `veltric_tenant_blocked`) deliberately do NOT live here — they have a
 * backend contract and live in `lib/auth/tokenStore` and
 * `lib/auth/tenantBlockedStore`. Calling `getPref("companyId")` will never
 * return the cookie because `companyId` is not in the migration allow-list.
 *
 * Migration: pre-existing keys were written to `document.cookie` by a now-
 * removed helper. On first read of an allow-listed key we transparently
 * promote the cookie value into localStorage and delete the cookie. The
 * promotion path is safe to remove after one or two release cycles.
 */

const COOKIE_PROMOTE_PREFIXES = [
  "filter-",
  "view-",
  "pageSize-",
  "dashboardId-",
  "templateDashboardId-",
  "meta-calendar-",
  "seating-",
] as const;

const COOKIE_PROMOTE_EXACT = new Set<string>([
  "viewMode",
  "recentEventIds",
  "collapsed",
  "NEXT_LOCALE",
]);

function canPromoteFromCookie(key: string): boolean {
  if (COOKIE_PROMOTE_EXACT.has(key)) return true;
  return COOKIE_PROMOTE_PREFIXES.some((p) => key.startsWith(p));
}

function readLegacyCookie(key: string): string | null {
  if (typeof document === "undefined") return null;
  const needle = `${key}=`;
  const parts = document.cookie ? document.cookie.split("; ") : [];
  for (const part of parts) {
    if (part.startsWith(needle)) {
      const raw = part.slice(needle.length);
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    }
  }
  return null;
}

function deleteLegacyCookie(key: string): void {
  if (typeof document === "undefined") return;
  document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

export function getPref(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    const ls = window.localStorage.getItem(key);
    if (ls !== null) return ls;
    if (!canPromoteFromCookie(key)) return null;
    const fromCookie = readLegacyCookie(key);
    if (fromCookie === null) return null;
    try {
      window.localStorage.setItem(key, fromCookie);
    } catch {
      // private mode / quota — keep returning the cookie value anyway
    }
    deleteLegacyCookie(key);
    return fromCookie;
  } catch {
    return null;
  }
}

export function setPref(key: string, value: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // private mode / quota exceeded — nothing we can do, drop silently
  }
}

export function removePref(key: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // noop
  }
  if (canPromoteFromCookie(key)) {
    deleteLegacyCookie(key);
  }
}

export function getJSONPref<T>(key: string, fallback: T): T {
  const raw = getPref(key);
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function setJSONPref(key: string, value: unknown): void {
  try {
    setPref(key, JSON.stringify(value));
  } catch {
    // value not serialisable — drop silently
  }
}

/**
 * Subscribe to cross-tab changes for a single key. The `storage` event only
 * fires in *other* tabs, so this is a no-op for the tab that performed the
 * write — same-tab consumers should still update via React state. Returns
 * an unsubscribe function.
 */
export function subscribePref(
  key: string,
  listener: (value: string | null) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = (e: StorageEvent) => {
    if (e.key !== key) return;
    if (e.storageArea && e.storageArea !== window.localStorage) return;
    listener(e.newValue);
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}
